import React, { useEffect, useState } from "react";
import { colors } from "@styles/colors";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { Stack, Text, YStack, XStack, Circle, Popover, Button, Sheet, ScrollView, Spinner } from "tamagui";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser, Address, InstallLocation } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { updateDraftOrder } from "@components/product/API/handleProductAPI";
import { buildXContextHeader } from "@utils/api/headers";
import { RiMapPinLine, RiCloseFill, RiAddLine } from "react-icons/ri";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse } from "axios";
import AddressFormSheet, { combinedAddress } from "@components/address/AddressFormSheet";
import type { AddAddressFormData } from "@components/address/AddAddressFlowSheet";
import { createCustomerAddress, getCreateAddressErrorMessage } from "@screen/QuickOrder/utils/mockApi";
import { appToast } from "@components/Toast";
import { findDuplicateAddress } from "@utils/func/addressDedup";
import type { DeliveryArea } from "@components/address/func/isAddressDeliverable";
import { track } from "@lib/tracker";

/** 草稿 gas item 中與安裝位/重對應相關的欄位（其餘照原值帶回 GasItemReqDto）。 */
type DraftGasItem = {
  type: string;
  price_id: number;
  gas_id: number;
  gas_type: string;
  kilogram: number;
  unit_price: number;
  qty: number;
  usage_location: string;
  note: string | null;
  floor: number;
  is_elevator: boolean;
  replacing_customer_delivery_id?: number | null;
  replacing_spec?: number | null;
};

/**
 * 切換配送地址時，把草稿 gas item 的安裝位重新對應到「新地址」。
 * 用 4-key（用途+樓層+電梯+備註）在新地址的 install_locations 找對應的 customer_delivery_id：
 *  - 找到 → 帶該 id（後端 reuse 既有安裝位）。
 *  - 找不到 → 不帶 id（後端驗證通過；送出時依 (customer_address_id, 用途, 樓層, 電梯) upsert 到新地址）。
 * replacing_*（換桶）僅存在性驗證、不綁地址，照原值帶回。
 */
const remapGasItemForAddress = (g: DraftGasItem, installLocations: InstallLocation[]) => {
  const match = installLocations.find(
    (loc) =>
      loc.usage_location === g.usage_location &&
      loc.floor === g.floor &&
      loc.is_elevator === g.is_elevator &&
      (loc.note ?? "").trim() === (g.note ?? "").trim(),
  );
  const base = {
    type: g.type,
    price_id: g.price_id,
    gas_id: g.gas_id,
    gas_type: g.gas_type,
    kilogram: g.kilogram,
    unit_price: g.unit_price,
    qty: g.qty,
    usage_location: g.usage_location,
    note: g.note ?? null,
    floor: g.floor,
    is_elevator: g.is_elevator,
    replacing_customer_delivery_id: g.replacing_customer_delivery_id ?? null,
    replacing_spec: g.replacing_spec ?? null,
  };
  return match?.customer_delivery_id != null ? { ...base, customer_delivery_id: match.customer_delivery_id } : base;
};

export function AddressSwitchMenu({
  themeColor = "white",
  changeColor,
  deliveryAreas,
  flush = false,
  radius = 16,
}: {
  themeColor?: string;
  changeColor?: string;
  /** 有 supplier context 的頁面（如 GasShop cart）傳入後，新增地址時會做配送範圍即時提示 */
  deliveryAreas?: DeliveryArea[];
  /** 觸發鈕不帶水平內距，讓地址文字與上方區塊標題切齊（瓦斯訂購 Skin V2 用）。 */
  flush?: boolean;
  /** 觸發鈕圓角。預設 16；瓦斯訂購 Skin V2 傳 20 以與帳戶切換鈕一致。 */
  radius?: number;
}) {
  /* themeColor="haze"：Skin V2 的淡霧頁首。原本這顆是「半透明白底 + 白字」，靠底下有一塊實藍；
     淡底上那組白會整個消失，故改成實白小卡（細邊線 + 深藍字），與頁內白卡同一種語彙。 */
  const haze = themeColor === "haze";
  const activeColor = haze ? v2.hazeInk : colors[themeColor as keyof typeof colors] || themeColor;
  const changeTextColor = changeColor ? colors[changeColor as keyof typeof colors] || changeColor : activeColor;
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity, setChooseIdentity } = useUser();
  const [resAddressList, setResAddressList] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 僅 owner 可新增地址（後端 POST /delivery/address 限 owner，member 會 403）
  const canAdd = chooseIdentity?.role === "owner";

  // 切換配送地址：先切 choose_address，再把既有草稿同步到新地址。
  // 後端 updateDraft 規則：只改 customer_address_id 而不送 gas_list，舊 gas item 會留著舊地址的
  // customer_delivery_id（送出時被擋）；故有瓦斯品項時一定要連 gas_list（重對應安裝位）一起 PUT。
  const handleSelectAddress = async (newAddress: Address) => {
    const prevAddress = chooseIdentity?.choose_address ?? null;
    if (String(prevAddress?.customer_address_id) !== String(newAddress.customer_address_id)) {
      track("address_select", { customer_address_id: newAddress.customer_address_id });
    }
    if (chooseIdentity) setChooseIdentity({ ...chooseIdentity, choose_address: newAddress });
    setOpen(false);

    const { draft, draftOrderId, syncDraft } = useOrder.getState();
    if (!draftOrderId || !draft) return;
    // 同地址免動
    if (String(draft.customer_address_id) === String(newAddress.customer_address_id)) return;

    const gasList: DraftGasItem[] = Array.isArray(draft.gas_list) ? draft.gas_list : [];
    const commodityList: unknown[] = Array.isArray(draft.commodity_list) ? draft.commodity_list : [];
    if (gasList.length === 0 && commodityList.length === 0) return; // 空草稿免動（避免「至少一筆」檢查）

    const body: { customer_address_id: number; gas_list?: ReturnType<typeof remapGasItemForAddress>[] } = {
      customer_address_id: Number(newAddress.customer_address_id),
    };
    if (gasList.length > 0) {
      const installLocations: InstallLocation[] = Array.isArray(newAddress.install_locations) ? newAddress.install_locations : [];
      body.gas_list = gasList.map((g) => remapGasItemForAddress(g, installLocations));
    }

    const res = await updateDraftOrder({
      draftId: draftOrderId,
      body,
      jwtToken: cusJwtToken || "",
      xContext: buildXContextHeader(chooseIdentity) || "",
    });
    if (res.state === "success") {
      const updated = res.data?.data?.draft ?? res.data?.data;
      if (updated) syncDraft(updated);
    } else if (chooseIdentity) {
      // PUT 失敗 → 還原 choose_address，避免 UI 顯示新地址但草稿仍綁舊地址（送出會送錯地址）。
      // updateDraftOrder 內已 toast 錯誤；草稿本身維持原狀。
      setChooseIdentity({ ...chooseIdentity, choose_address: prevAddress });
    }
  };

  // 新增地址：共用 AddressFormSheet（表單 + 地圖，無搜尋步驟）→ 建立後加入清單並選用
  const handleAddAddress = async (data: AddAddressFormData) => {
    if (submitting) return;
    const display = combinedAddress({
      city: data.city || "",
      area: data.area || "",
      road: data.road || "",
      streetNumber: data.streetNumber,
      floor: data.floor,
      room: data.room,
    });
    const fullAddress = data.otherAddress?.trim() ? `${display} ${data.otherAddress}` : display;

    // 重複地址：清單已有相同 full_address → 不新增，提醒並直接選用既有那筆
    const duplicate = findDuplicateAddress(resAddressList, fullAddress);
    if (duplicate) {
      if (chooseIdentity) setChooseIdentity({ ...chooseIdentity, choose_address: duplicate });
      setOpenAdd(false);
      setOpen(false);
      appToast.error("此地址已存在，已為您選用");
      return;
    }

    setSubmitting(true);
    try {
      const res = await createCustomerAddress({
        addressInfos: {
          city: data.city || undefined,
          site_id: data.area || undefined,
          road: data.road || undefined,
          address_number: data.streetNumber?.trim() || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          extra: data.otherAddress || undefined,
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
        },
      });
      const newId = res.customerAddressResult.customer_address_id;
      const newAddr: Address = {
        customer_address_id: newId,
        full_address: fullAddress,
        city: data.city,
        district: data.area,
        floor: data.floor,
      };
      setResAddressList((prev) => [...prev, newAddr]);
      if (chooseIdentity) setChooseIdentity({ ...chooseIdentity, choose_address: newAddr });
      setOpenAdd(false);
      setOpen(false);
      appToast.success("已新增並選用此地址");
    } catch (err) {
      throw new Error(getCreateAddressErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const xContext = buildXContextHeader(chooseIdentity);
    if (cusJwtToken && xContext) {
      setLoading(true);
      handleRequest({
        asyncRequest: () =>
          api.get("/app-api/platform-order-drafts/bootstrap", {
            headers: {
              Authorization: `Bearer ${cusJwtToken}`,
              "X-Context": xContext,
            },
          }),
        successCallback: (res: AxiosResponse) => {
          const { data } = res.data;
          const addresses = data?.available_addresses;
          if (Array.isArray(addresses)) {
            setResAddressList(addresses);
            if (chooseIdentity && addresses.length > 0) {
              const cur = chooseIdentity.choose_address;
              if (!cur) {
                // 如果目前沒有選擇地址，預設選第一個
                setChooseIdentity({ ...chooseIdentity, choose_address: addresses[0] });
              } else if (!Number.isInteger(Number(cur.customer_address_id))) {
                // onboarding 定位塞的「佔位地址」（customer_address_id="gps"/"onboarding"）不是真實 id，
                // 建 draft 會被後端擋（customer_address_id must be an integer）。拿到真實地址清單時
                // 自動治癒：以 full_address 對回真實那筆；對不到就取第一筆。
                const matched = addresses.find((a: { full_address?: string }) => a.full_address === cur.full_address);
                setChooseIdentity({ ...chooseIdentity, choose_address: matched || addresses[0] });
              }
            }
          }
        },
        finalCallback: () => {
          setLoading(false);
        },
      });
    }
  }, [cusJwtToken, chooseIdentity?.context_id]);

  const currentAddress = chooseIdentity?.choose_address?.full_address || resAddressList[0]?.full_address;

  return (
    <>
      <Button
        backgroundColor={haze ? "#fff" : "rgba(255, 255, 255, 0.2)"}
        p={10}
        px={flush ? 0 : 10}
        py={flush ? 0 : 10}
        // flush（結帳頁卡片內）高度對齊隔壁「配送時間」那種純文字列的行盒高度，
        // 否則 Button 自帶的上下留白會讓「配送地址」這一段的空隙比其他段大一截。
        h={flush ? 21 : 40}
        width="100%"
        borderRadius={radius}
        hoverStyle={{ backgroundColor: haze ? "#f7fbff" : "rgba(255, 255, 255, 0.2)" }}
        pressStyle={{ backgroundColor: haze ? "#f2f7fd" : "rgba(255, 255, 255, 0.2)", opacity: 0.8 }}
        borderColor={haze ? v2.cardBorder : "rgba(255,255,255,0.2)"}
        borderWidth="$0.2"
        onPress={() => setOpen(true)}>
        <XStack justifyContent="space-between" ai="center" width="100%">
          <XStack ai="center" gap={8} flex={1}>
            {(themeColor === "white" || haze) && (
              <Circle size={20} backgroundColor={haze ? v2.primarySoft : "rgba(255, 255, 255, 0.5)"} pressStyle={{ opacity: 0.8 }}>
                <RiMapPinLine color={activeColor} size={12} />
              </Circle>
            )}
            {loading ? (
              <Spinner size="small" color={activeColor} />
            ) : currentAddress ? (
              <Text col={activeColor} fontSize={14} fontWeight="bold" numberOfLines={1} flex={1} textAlign="left">
                {currentAddress}
              </Text>
            ) : (
              <Text col={activeColor} fontSize={14} fontWeight="bold" numberOfLines={1} flex={1} textAlign="left">
                尚未設定地址
              </Text>
            )}
          </XStack>
          <Text color={changeTextColor} fontSize={14} fontWeight="bold" ml="$2">
            變更
          </Text>
        </XStack>
      </Button>
      <Sheet
        disableDrag
        modal
        open={open}
        onOpenChange={setOpen}
        snapPoints={[95]}
        dismissOnSnapToBottom={false}
        dismissOnOverlayPress={false}
        animation="medium">
        <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} backgroundColor="rgba(0,0,0,0.4)" />
        <Sheet.Handle />
        <Sheet.Frame bg="#ffffff" borderTopLeftRadius={24} borderTopRightRadius={24}>
          <XStack alignItems="center" justifyContent="space-between" padding="$5">
            <YStack>
              <Text fontWeight="bold">設定配送地址</Text>
              <Text fontSize={14} color={colors.gray}>
                搜尋地址並調整精確位置
              </Text>
            </YStack>
            <Button
              p={4}
              chromeless
              onPress={() => setOpen(false)}
              backgroundColor={colors.primaryActive}
              pressStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}
              hoverStyle={{ backgroundColor: colors.primaryActive, borderColor: colors.primaryActive, borderWidth: 1 }}>
              <RiCloseFill size={22} />
            </Button>
          </XStack>
          <ScrollView showsVerticalScrollIndicator={false} background={colors.bg}>
            <YStack gap={3} p="$3">
              {loading ? (
                <XStack jc="center" py="$5">
                  <Spinner size="large" color={colors.primary} />
                </XStack>
              ) : resAddressList.length > 0 ? (
                resAddressList.map((item) => {
                  const isSelected = item.customer_address_id === chooseIdentity?.choose_address?.customer_address_id;
                  return (
                    <button
                      key={item.customer_address_id}
                      type="button"
                      onClick={() => {
                        void handleSelectAddress(item);
                      }}
                      className={`flex items-center gap-3 px-3 py-3 mt-3 rounded-xl border w-full text-left transition-colors ${
                        isSelected ? "border-blue-200 bg-blue-50 active:bg-blue-100" : "border-gray-200 bg-gray-50 active:bg-gray-100"
                      }`}>
                      <RiMapPinLine className={`w-5 h-5 flex-shrink-0 ${isSelected ? "text-blue-500" : "text-gray-400"}`} />
                      <div>
                        <div className={`font-semibold text-sm leading-snug ${isSelected ? "text-gray-800" : "text-gray-500"}`}>
                          {item?.full_address}
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <YStack ai="center" jc="center" py="$10">
                  <Text color={colors.gray}>目前尚無地址資料</Text>
                </YStack>
              )}

              {/* 新增地址（與註冊流程相同的搜尋→表單 modal）。僅 owner 顯示 */}
              {canAdd && (
                <button
                  type="button"
                  onClick={() => setOpenAdd(true)}
                  className="flex items-center justify-center gap-2 px-3 py-3 mt-3 rounded-xl border border-dashed border-blue-300 bg-blue-50 active:bg-blue-100 w-full text-blue-600 font-semibold text-sm">
                  <RiAddLine className="w-5 h-5" />
                  新增地址
                </button>
              )}
            </YStack>
          </ScrollView>
        </Sheet.Frame>
      </Sheet>

      <AddressFormSheet isOpen={openAdd} onOpenChange={setOpenAdd} onSelect={handleAddAddress} deliveryAreas={deliveryAreas} />
    </>
  );
}
